import { ADUModel, PoolModel, DesignObject } from '../../store/designStore'

// Compliance check result types
export interface ComplianceResult {
  id: string
  type: 'zoning' | 'building_code' | 'ada' | 'fire_safety' | 'utility' | 'environmental'
  status: 'compliant' | 'warning' | 'violation' | 'requires_permit'
  severity: 'low' | 'medium' | 'high' | 'critical'
  title: string
  description: string
  recommendation?: string
  code_reference?: string
  estimated_cost?: number
  required_permits?: string[]
}

export interface ComplianceReport {
  overall_status: 'compliant' | 'needs_attention' | 'major_violations'
  compliance_score: number // 0-100
  total_checks: number
  passed_checks: number
  warnings: number
  violations: number
  results: ComplianceResult[]
  estimated_permit_cost: number
  estimated_compliance_cost: number
}

export interface ZoningRules {
  district: string
  max_lot_coverage: number // percentage
  max_building_height: number // feet
  min_setbacks: {
    front: number
    rear: number
    side: number
  }
  max_adu_size: number // square feet
  adu_allowed: boolean
  pool_allowed: boolean
  parking_requirements: {
    adu: number // spaces required
    main_dwelling: number
  }
  max_impervious_surface: number // percentage
}

export interface BuildingCodes {
  min_ceiling_height: number // feet
  min_room_sizes: {
    bedroom: number // square feet
    bathroom: number
    kitchen: number
    living_room: number
  }
  window_requirements: {
    min_natural_light: number // percentage of floor area
    egress_window_required: boolean
  }
  electrical_requirements: {
    min_outlets_per_room: number
    gfci_required_areas: string[]
  }
  plumbing_requirements: {
    water_pressure: number // PSI
    sewer_connection_required: boolean
  }
  fire_safety: {
    smoke_detectors_required: boolean
    fire_extinguisher_required: boolean
    egress_requirements: boolean
  }
}

export class ComplianceChecker {
  private zoningRules: ZoningRules
  private buildingCodes: BuildingCodes
  private propertyInfo: {
    lot_size: number // square feet
    existing_coverage: number // square feet
    existing_impervious: number // square feet
  }

  constructor(
    zoningRules: ZoningRules,
    buildingCodes: BuildingCodes,
    propertyInfo: { lot_size: number; existing_coverage: number; existing_impervious: number }
  ) {
    this.zoningRules = zoningRules
    this.buildingCodes = buildingCodes
    this.propertyInfo = propertyInfo
  }

  // Main compliance check method
  checkCompliance(objects: DesignObject[]): ComplianceReport {
    const results: ComplianceResult[] = []

    // Run all compliance checks
    results.push(...this.checkZoningCompliance(objects))
    results.push(...this.checkBuildingCodeCompliance(objects))
    results.push(...this.checkADACompliance(objects))
    results.push(...this.checkFireSafetyCompliance(objects))
    results.push(...this.checkUtilityCompliance(objects))
    results.push(...this.checkEnvironmentalCompliance(objects))

    // Calculate overall metrics
    const total_checks = results.length
    const passed_checks = results.filter(r => r.status === 'compliant').length
    const warnings = results.filter(r => r.status === 'warning').length
    const violations = results.filter(r => r.status === 'violation').length

    const compliance_score = total_checks > 0 ? Math.round((passed_checks / total_checks) * 100) : 100

    let overall_status: 'compliant' | 'needs_attention' | 'major_violations'
    if (violations > 0) {
      overall_status = 'major_violations'
    } else if (warnings > 0) {
      overall_status = 'needs_attention'
    } else {
      overall_status = 'compliant'
    }

    // Calculate estimated costs
    const estimated_permit_cost = this.calculatePermitCosts(results)
    const estimated_compliance_cost = this.calculateComplianceCosts(results)

    return {
      overall_status,
      compliance_score,
      total_checks,
      passed_checks,
      warnings,
      violations,
      results,
      estimated_permit_cost,
      estimated_compliance_cost
    }
  }

  // Zoning compliance checks
  private checkZoningCompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    // Check ADU allowance
    const adus = objects.filter(obj => obj.type === 'ADU') as ADUModel[]
    if (adus.length > 0 && !this.zoningRules.adu_allowed) {
      results.push({
        id: 'zoning-adu-not-allowed',
        type: 'zoning',
        status: 'violation',
        severity: 'critical',
        title: 'ADU Not Permitted in Zoning District',
        description: `ADUs are not allowed in ${this.zoningRules.district} zoning district`,
        recommendation: 'Consider applying for a zoning variance or choose a different property',
        code_reference: `${this.zoningRules.district} Zoning Code Section 4.2.1`
      })
    }

    // Check pool allowance
    const pools = objects.filter(obj => obj.type === 'Pool') as PoolModel[]
    if (pools.length > 0 && !this.zoningRules.pool_allowed) {
      results.push({
        id: 'zoning-pool-not-allowed',
        type: 'zoning',
        status: 'violation',
        severity: 'high',
        title: 'Pool Not Permitted in Zoning District',
        description: `Pools are not allowed in ${this.zoningRules.district} zoning district`,
        recommendation: 'Check with local planning department for exceptions',
        code_reference: `${this.zoningRules.district} Zoning Code Section 5.1.3`
      })
    }

    // Check lot coverage
    const total_building_area = this.calculateTotalBuildingArea(objects)
    const total_coverage = this.propertyInfo.existing_coverage + total_building_area
    const coverage_percentage = (total_coverage / this.propertyInfo.lot_size) * 100

    if (coverage_percentage > this.zoningRules.max_lot_coverage) {
      results.push({
        id: 'zoning-lot-coverage',
        type: 'zoning',
        status: 'violation',
        severity: 'high',
        title: 'Lot Coverage Exceeded',
        description: `Total lot coverage of ${coverage_percentage.toFixed(1)}% exceeds maximum allowed ${this.zoningRules.max_lot_coverage}%`,
        recommendation: 'Reduce building size or consider a smaller design',
        code_reference: 'Zoning Code Section 3.4.2',
        estimated_cost: 5000 // Cost to redesign
      })
    } else if (coverage_percentage > this.zoningRules.max_lot_coverage * 0.9) {
      results.push({
        id: 'zoning-lot-coverage-warning',
        type: 'zoning',
        status: 'warning',
        severity: 'medium',
        title: 'Lot Coverage Near Limit',
        description: `Total lot coverage of ${coverage_percentage.toFixed(1)}% is close to maximum allowed ${this.zoningRules.max_lot_coverage}%`,
        recommendation: 'Consider future expansion needs before finalizing design'
      })
    } else {
      results.push({
        id: 'zoning-lot-coverage-ok',
        type: 'zoning',
        status: 'compliant',
        severity: 'low',
        title: 'Lot Coverage Compliant',
        description: `Total lot coverage of ${coverage_percentage.toFixed(1)}% is within allowed ${this.zoningRules.max_lot_coverage}%`
      })
    }

    // Check setbacks for each object
    objects.forEach((obj, index) => {
      const setbackResults = this.checkSetbacks(obj, index)
      results.push(...setbackResults)
    })

    // Check ADU size limits
    adus.forEach((adu, index) => {
      const aduArea = adu.dimensions.width * adu.dimensions.depth
      if (aduArea > this.zoningRules.max_adu_size) {
        results.push({
          id: `zoning-adu-size-${index}`,
          type: 'zoning',
          status: 'violation',
          severity: 'high',
          title: 'ADU Size Exceeds Limit',
          description: `ADU size of ${aduArea} sq ft exceeds maximum allowed ${this.zoningRules.max_adu_size} sq ft`,
          recommendation: 'Reduce ADU dimensions to meet zoning requirements',
          code_reference: 'ADU Ordinance Section 2.3.1',
          estimated_cost: 3000
        })
      }
    })

    return results
  }

  // Building code compliance checks
  private checkBuildingCodeCompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    const adus = objects.filter(obj => obj.type === 'ADU') as ADUModel[]

    adus.forEach((adu, index) => {
      // Check ceiling height
      if (adu.dimensions.height < this.buildingCodes.min_ceiling_height) {
        results.push({
          id: `building-ceiling-height-${index}`,
          type: 'building_code',
          status: 'violation',
          severity: 'high',
          title: 'Insufficient Ceiling Height',
          description: `ADU ceiling height of ${adu.dimensions.height}' is below minimum required ${this.buildingCodes.min_ceiling_height}'`,
          recommendation: 'Increase ceiling height or consider a different design',
          code_reference: 'Building Code Section R305.1',
          estimated_cost: 2000
        })
      }

      // Check room sizes
      const totalArea = adu.dimensions.width * adu.dimensions.depth
      const estimatedBedroomArea = totalArea * 0.3 // Rough estimate

      if (adu.features.bedrooms > 0 && estimatedBedroomArea < this.buildingCodes.min_room_sizes.bedroom) {
        results.push({
          id: `building-bedroom-size-${index}`,
          type: 'building_code',
          status: 'warning',
          severity: 'medium',
          title: 'Bedroom Size May Be Insufficient',
          description: `Estimated bedroom area may be below minimum ${this.buildingCodes.min_room_sizes.bedroom} sq ft`,
          recommendation: 'Verify bedroom dimensions meet code requirements',
          code_reference: 'Building Code Section R304.2'
        })
      }

      // Check egress windows for bedrooms
      if (adu.features.bedrooms > 0 && !this.buildingCodes.window_requirements.egress_window_required) {
        results.push({
          id: `building-egress-window-${index}`,
          type: 'building_code',
          status: 'requires_permit',
          severity: 'medium',
          title: 'Egress Window Required',
          description: 'Bedrooms require egress windows for emergency exit',
          recommendation: 'Include egress windows in bedroom design',
          code_reference: 'Building Code Section R310.1',
          required_permits: ['Building Permit'],
          estimated_cost: 1500
        })
      }
    })

    return results
  }

  // ADA compliance checks
  private checkADACompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    const adus = objects.filter(obj => obj.type === 'ADU') as ADUModel[]

    adus.forEach((adu, index) => {
      // Check if ADA compliance is needed (simplified check)
      const needsADACompliance = adu.dimensions.width * adu.dimensions.depth > 800 // Larger ADUs

      if (needsADACompliance) {
        results.push({
          id: `ada-accessibility-${index}`,
          type: 'ada',
          status: 'requires_permit',
          severity: 'medium',
          title: 'ADA Accessibility Features Recommended',
          description: 'Large ADUs should consider ADA accessibility features',
          recommendation: 'Include accessible entrance, wider doorways, and accessible bathroom',
          code_reference: 'ADA Standards Section 4.13',
          estimated_cost: 4000
        })
      }

      // Check entrance accessibility
      results.push({
        id: `ada-entrance-${index}`,
        type: 'ada',
        status: 'warning',
        severity: 'low',
        title: 'Accessible Entrance Recommended',
        description: 'Consider providing an accessible entrance path',
        recommendation: 'Ensure entrance has maximum 1:20 slope and minimum 36" width'
      })
    })

    return results
  }

  // Fire safety compliance checks
  private checkFireSafetyCompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    const adus = objects.filter(obj => obj.type === 'ADU') as ADUModel[]

    adus.forEach((adu, index) => {
      // Smoke detectors
      if (this.buildingCodes.fire_safety.smoke_detectors_required) {
        results.push({
          id: `fire-smoke-detectors-${index}`,
          type: 'fire_safety',
          status: 'requires_permit',
          severity: 'high',
          title: 'Smoke Detectors Required',
          description: 'Smoke detectors must be installed in all sleeping areas and hallways',
          recommendation: 'Install hardwired smoke detectors with battery backup',
          code_reference: 'Fire Code Section 907.2.11',
          required_permits: ['Electrical Permit'],
          estimated_cost: 300
        })
      }

      // Fire extinguisher
      if (this.buildingCodes.fire_safety.fire_extinguisher_required) {
        results.push({
          id: `fire-extinguisher-${index}`,
          type: 'fire_safety',
          status: 'warning',
          severity: 'low',
          title: 'Fire Extinguisher Recommended',
          description: 'Consider installing a fire extinguisher in kitchen area',
          recommendation: 'Install 2A:10B:C rated fire extinguisher',
          estimated_cost: 50
        })
      }
    })

    return results
  }

  // Utility compliance checks
  private checkUtilityCompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    const adus = objects.filter(obj => obj.type === 'ADU') as ADUModel[]
    const pools = objects.filter(obj => obj.type === 'Pool') as PoolModel[]

    // Check electrical capacity
    if (adus.length > 0 || pools.length > 0) {
      results.push({
        id: 'utility-electrical-capacity',
        type: 'utility',
        status: 'requires_permit',
        severity: 'medium',
        title: 'Electrical Service Upgrade May Be Required',
        description: 'Additional structures may require electrical service upgrade',
        recommendation: 'Have licensed electrician assess current electrical capacity',
        code_reference: 'Electrical Code Section 220.82',
        required_permits: ['Electrical Permit'],
        estimated_cost: 2500
      })
    }

    // Check water/sewer capacity
    if (adus.length > 0) {
      results.push({
        id: 'utility-water-sewer',
        type: 'utility',
        status: 'warning',
        severity: 'medium',
        title: 'Water/Sewer Capacity Assessment Needed',
        description: 'ADU will increase water usage and sewer load',
        recommendation: 'Verify existing water pressure and sewer capacity',
        estimated_cost: 500
      })
    }

    return results
  }

  // Environmental compliance checks
  private checkEnvironmentalCompliance(objects: DesignObject[]): ComplianceResult[] {
    const results: ComplianceResult[] = []

    // Check impervious surface coverage
    const additionalImpervious = this.calculateImperviousSurface(objects)
    const totalImpervious = this.propertyInfo.existing_impervious + additionalImpervious
    const imperviousPercentage = (totalImpervious / this.propertyInfo.lot_size) * 100

    if (imperviousPercentage > this.zoningRules.max_impervious_surface) {
      results.push({
        id: 'environmental-impervious-surface',
        type: 'environmental',
        status: 'violation',
        severity: 'medium',
        title: 'Impervious Surface Limit Exceeded',
        description: `Total impervious surface of ${imperviousPercentage.toFixed(1)}% exceeds maximum ${this.zoningRules.max_impervious_surface}%`,
        recommendation: 'Consider permeable paving materials or reduce hardscape area',
        code_reference: 'Stormwater Management Ordinance Section 3.2',
        estimated_cost: 3000
      })
    }

    // Stormwater management
    if (additionalImpervious > 500) { // 500 sq ft threshold
      results.push({
        id: 'environmental-stormwater',
        type: 'environmental',
        status: 'requires_permit',
        severity: 'medium',
        title: 'Stormwater Management Plan Required',
        description: 'Projects adding significant impervious surface require stormwater management',
        recommendation: 'Develop stormwater management plan with rain gardens or detention',
        required_permits: ['Stormwater Permit'],
        estimated_cost: 2000
      })
    }

    return results
  }

  // Helper methods
  private checkSetbacks(obj: DesignObject, index: number): ComplianceResult[] {
    const results: ComplianceResult[] = []
    
    // Simplified setback check - in real implementation, would need property boundaries
    const position = obj.position
    const dimensions = obj.dimensions

    // Assuming property center is at (0,0) and boundaries are at ±40 width, ±60 depth
    const propertyBounds = { width: 80, depth: 120 }
    
    const frontSetback = (propertyBounds.depth/2 + position[2] - dimensions.depth/2)
    const rearSetback = (propertyBounds.depth/2 - position[2] - dimensions.depth/2)
    const leftSetback = (propertyBounds.width/2 + position[0] - dimensions.width/2)
    const rightSetback = (propertyBounds.width/2 - position[0] - dimensions.width/2)

    const minSetbacks = this.zoningRules.min_setbacks

    if (frontSetback < minSetbacks.front) {
      results.push({
        id: `setback-front-${index}`,
        type: 'zoning',
        status: 'violation',
        severity: 'high',
        title: 'Front Setback Violation',
        description: `Front setback of ${frontSetback.toFixed(1)}' is less than required ${minSetbacks.front}'`,
        recommendation: 'Move structure away from front property line',
        code_reference: 'Zoning Code Section 4.5.1'
      })
    }

    if (rearSetback < minSetbacks.rear) {
      results.push({
        id: `setback-rear-${index}`,
        type: 'zoning',
        status: 'violation',
        severity: 'high',
        title: 'Rear Setback Violation',
        description: `Rear setback of ${rearSetback.toFixed(1)}' is less than required ${minSetbacks.rear}'`,
        recommendation: 'Move structure away from rear property line',
        code_reference: 'Zoning Code Section 4.5.2'
      })
    }

    if (leftSetback < minSetbacks.side || rightSetback < minSetbacks.side) {
      results.push({
        id: `setback-side-${index}`,
        type: 'zoning',
        status: 'violation',
        severity: 'high',
        title: 'Side Setback Violation',
        description: `Side setback violation - minimum ${minSetbacks.side}' required`,
        recommendation: 'Move structure away from side property lines',
        code_reference: 'Zoning Code Section 4.5.3'
      })
    }

    return results
  }

  private calculateTotalBuildingArea(objects: DesignObject[]): number {
    return objects.reduce((total, obj) => {
      if (obj.type === 'ADU') {
        return total + (obj.dimensions.width * obj.dimensions.depth)
      }
      return total
    }, 0)
  }

  private calculateImperviousSurface(objects: DesignObject[]): number {
    return objects.reduce((total, obj) => {
      if (obj.type === 'ADU') {
        return total + (obj.dimensions.width * obj.dimensions.depth)
      } else if (obj.type === 'Pool') {
        // Pool + typical deck area
        const poolArea = obj.dimensions.width * (obj.dimensions as any).length
        const deckArea = poolArea * 0.5 // Estimate 50% additional deck area
        return total + poolArea + deckArea
      }
      return total
    }, 0)
  }

  private calculatePermitCosts(results: ComplianceResult[]): number {
    const permitResults = results.filter(r => r.status === 'requires_permit')
    let totalCost = 0

    const permitCosts: Record<string, number> = {
      'Building Permit': 500,
      'Electrical Permit': 150,
      'Plumbing Permit': 100,
      'Mechanical Permit': 100,
      'Stormwater Permit': 300
    }

    permitResults.forEach(result => {
      if (result.required_permits) {
        result.required_permits.forEach(permit => {
          totalCost += permitCosts[permit] || 200
        })
      }
    })

    return totalCost
  }

  private calculateComplianceCosts(results: ComplianceResult[]): number {
    return results.reduce((total, result) => {
      return total + (result.estimated_cost || 0)
    }, 0)
  }
}

// Default zoning rules for demo
export const DEFAULT_ZONING_RULES: ZoningRules = {
  district: 'R-1 Single Family Residential',
  max_lot_coverage: 35,
  max_building_height: 16,
  min_setbacks: {
    front: 25,
    rear: 10,
    side: 5
  },
  max_adu_size: 1200,
  adu_allowed: true,
  pool_allowed: true,
  parking_requirements: {
    adu: 1,
    main_dwelling: 2
  },
  max_impervious_surface: 45
}

// Default building codes for demo
export const DEFAULT_BUILDING_CODES: BuildingCodes = {
  min_ceiling_height: 8,
  min_room_sizes: {
    bedroom: 70,
    bathroom: 30,
    kitchen: 50,
    living_room: 120
  },
  window_requirements: {
    min_natural_light: 10,
    egress_window_required: true
  },
  electrical_requirements: {
    min_outlets_per_room: 2,
    gfci_required_areas: ['bathroom', 'kitchen', 'outdoor']
  },
  plumbing_requirements: {
    water_pressure: 40,
    sewer_connection_required: true
  },
  fire_safety: {
    smoke_detectors_required: true,
    fire_extinguisher_required: false,
    egress_requirements: true
  }
}

export default ComplianceChecker
